import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';

import EventBusInstance from '@core/eventbus';
import mediaAssetServiceInstance from '@services/MediaAssetService';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import D3Throttle, { debounce } from '@utilities/throttle';

export type PluginOptions = SwiperOptions & {
  containerSelector?: false | string;
  slideSelector?: false | string;
  playVideoInPage?: boolean;
  minWidth?: number;
  maxWidth?: number;
  tracking?: {
    category: string;
    action?: string;
    label?: string;
  };
};

export default class ResponsiveCarouselPlugin {
  options: PluginOptions;
  instance?: Swiper;
  dom: HTMLElement;
  container: HTMLElement;
  wrapper: HTMLElement;
  prevEl: HTMLElement;
  nextEl: HTMLElement;

  videoItems: HTMLElement[];
  videoIds: string[];
  arrowClicked: boolean;

  throttle: D3Throttle;

  constructor(dom: HTMLElement, o: PluginOptions) {
    const defaults: PluginOptions = {
      modules: [Navigation, Pagination, Autoplay, EffectFade],
      containerSelector: false,
      slideSelector: false,
      autoHeight: true,
      watchOverflow: true,
      slidesPerView: 'auto',
      observer: false,
      observeParents: false,
      observeSlideChildren: false,
      playVideoInPage: false,
    };

    this.options = {
      ...defaults,
      ...o,
    };

    this.dom = dom;
    if (this.options.containerSelector) this.dom.querySelector<HTMLElement>(this.options.containerSelector);

    this.container = this.dom.querySelector<HTMLElement>('.js-swiper');
    this.wrapper = this.dom.querySelector<HTMLElement>('.js-swiper-wrapper');
    this.prevEl = this.dom.querySelector<HTMLElement>('.swiper-button-prev');
    this.nextEl = this.dom.querySelector<HTMLElement>('.swiper-button-next');
  }

  init() {
    const events = ['orientationchange.responsivecarousel', 'resize.responsivecarousel'];
    this.throttle = new D3Throttle(window, this._checkWidth, undefined, events, this);
    this._checkWidth();

    this.prevEl && this.prevEl.addEventListener('click', () => (this.arrowClicked = true));
    this.nextEl && this.nextEl.addEventListener('click', () => (this.arrowClicked = true));

    if (mediaAssetServiceInstance.isActive) {
      this.videoItems = Array.from(this.dom.querySelectorAll<HTMLElement>('.js-video-item'));
      this.videoIds = this.videoItems.map((el) => el.dataset.videoid);
      this.videoIds.length && mediaAssetServiceInstance.on(this.videoIds, this._checkVideos, this);
    }
  }

  detach() {
    this.throttle.off();
    EventBusInstance.off('tabchange.responsivecarousel', this._goToActive, this);
    this.videoIds?.length && mediaAssetServiceInstance.off(this.videoIds, this._checkVideos, this);
    this._toggleCarousel(false);
  }

  _checkWidth() {
    const { availWidth: width } = screen;

    const carouselActive =
      (width >= this.options.minWidth || !this.options.minWidth) &&
      (width <= this.options.maxWidth || !this.options.maxWidth);
    this._toggleCarousel(carouselActive);
  }

  _toggleCarousel(carouselActive: boolean) {
    this.dom.classList.toggle('swiper-carousel', carouselActive);
    this.container.classList.toggle('swiper', carouselActive);
    this.wrapper.classList.toggle('swiper-wrapper', carouselActive);

    const slides = this.options.slideSelector
      ? Array.from(this.wrapper.querySelectorAll<HTMLElement>(this.options.slideSelector))
      : (Array.from(this.wrapper.children) as HTMLElement[]);
    slides.forEach((e) => e.classList.toggle('swiper-slide', carouselActive && !this._isHidden(e)));

    if (carouselActive) {
      this._initCarousel();
    } else {
      this._destroyCarousel();
    }
  }

  _isHidden(el: Element) {
    const style = window.getComputedStyle(el);
    return style.display === 'none';
  }

  _initCarousel(force?: boolean) {
    if (force) this._destroyCarousel();

    if (!this.instance) {
      const carouselOptions: SwiperOptions = {
        ...this.options,
        navigation: {
          nextEl: this.nextEl,
          prevEl: this.prevEl,
        },
        pagination: {
          el: this.dom.querySelector<HTMLElement>('.swiper-pagination'),
          type: 'bullets',
        },
        simulateTouch: !this.options.playVideoInPage,
        on: {
          slideChange: this.onSlideChange.bind(this),
          slideNextTransitionEnd: this.onSlideNextTransitionEnd.bind(this),
          slidePrevTransitionEnd: this.onSlidePrevTransitionEnd.bind(this),
          init: this._onInit.bind(this),
        },
      };

      this.instance = new Swiper(this.container, carouselOptions);
    }

    this._updateCarousel();
  }

  _updateCarousel = debounce(
    (carousel: Swiper = this.instance) => {
      carousel?.update();
      EventBusInstance.dispatch('requestLazyRecalc');
    },
    250,
    false,
    this,
  );

  _onInit = (carousel: Swiper) => {
    this._updateCarousel(carousel);
    this._goToActive(carousel);
    this._hideRepeatingItems();
    EventBusInstance.on('tabchange.responsivecarousel', this._goToActive, this);
  };

  _goToActive = (carousel = this.instance) => {
    const activeItem = this.dom.querySelector('.swiper-slide.active');
    if (!activeItem) return;

    const activeIndex = Array.from(this.dom.querySelectorAll('.swiper-slide')).findIndex((el) => el === activeItem);
    activeIndex >= 0 && carousel.slideTo(activeIndex);
  };

  _destroyCarousel() {
    this.instance?.destroy(true, true);
    delete this.instance;
  }

  _checkVideos(videos) {
    let refresh = false;
    this.videoIds.forEach((id) => {
      const video = videos.find((v) => v.mediaAssetId === `${id}`);
      const available = video && video.status === 'AVAILABLE';
      const element = this.videoItems.find((e) => e.matches(`[data-videoid="${id}"]`));

      if (element.classList.contains('hidden') !== !available) {
        refresh = true;
        element.classList.toggle('hidden', !available);
      }
    });

    refresh && this._checkWidth();
  }

  _trackEvent(direction) {
    if (this.instance?.activeIndex === this.instance?.previousIndex) return;
    if (this.arrowClicked === true) {
      GoogleTagManagerHelper.ga4Event('swipe_carousel', {
        interaction: this.options.tracking.action,
        section_reference: `Swipe carousel - ${this.options.tracking.action} - ${direction}`,
      });
      this.arrowClicked = false;
    } else {
      GoogleTagManagerHelper.ga4Event('swipe_carousel', {
        interaction: this.options.tracking.action,
        section_reference: `Swipe carousel - ${this.options.tracking.action}`,
      });
    }
  }

  _hideRepeatingItems() {
    const prevValue = {};
    Array.from(this.dom.querySelectorAll<HTMLElement>('.js__show-only-first')).forEach((el) => {
      const key = el.dataset.key ?? 'default';
      const value = el.dataset.value;
      el.classList.toggle('hidden', prevValue[key] === value);
      prevValue[key] = value;
    });
  }

  onSlideChange() {
    const videoElement = this.dom.querySelector('.jwplayer');
    if (videoElement && window.jwplayer(videoElement)) {
      window.jwplayer(videoElement).pause(true);
    }
  }

  onSlideNextTransitionEnd() {
    if (this.options.tracking?.action) {
      this._trackEvent('Next');
    }
  }

  onSlidePrevTransitionEnd() {
    if (this.options.tracking?.action) {
      this._trackEvent('Prev');
    }
  }
}
